import { useState } from "react";
import { DemoLogins } from "@/components/auth/DemoLogins";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/components/auth/VerifyPanel";

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <span className="text-[10px] text-slate-600">{hint}</span>}
  </label>
);

export const inputCls = "w-full bg-[#0A0C14] border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-purple-500/40 placeholder:text-slate-600";

export function LoginForm({ next, adminOnly = false }) {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (forgot) {
        const r = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(r.data.message);
        if (r.data.dev_link) setDevLink(r.data.dev_link);
      } else {
        const r = await api.post("/auth/login", form);
        if (adminOnly && !["founder", "admin"].includes(r.data.role)) {
          await api.post("/auth/logout").catch(() => {});
          throw { response: { data: { detail: "This account is not an admin. Use the consumer sign-in." } } };
        }
        setUser(r.data);
        navigate(next || "/studio", { replace: true });
      }
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
    <form data-testid="login-form" onSubmit={submit} className="space-y-4">
      <Field label="Email"><input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@brand.com" /></Field>
      {!forgot && (
        <Field label="Password">
          <div className="relative">
            <input data-testid="login-password" type={show ? "text" : "password"} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="••••••••" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-2.5 text-slate-500 hover:text-white">{show ? <EyeOff size={15} /> : <Eye size={15} />}</button>
          </div>
        </Field>
      )}
      {devLink && <a data-testid="dev-reset-link" href={devLink} className="block text-[10px] font-mono text-amber-300 bg-amber-500/10 rounded-lg px-3 py-2 break-all">Dev mode (email not configured) · open reset link</a>}
      <button data-testid="login-submit" type="submit" disabled={busy} className="w-full py-3 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-600 to-cyan-600 text-white inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && <Loader2 size={14} className="animate-spin" />} {forgot ? "Send reset link" : "Sign in"}
      </button>
      <button type="button" data-testid="toggle-forgot" onClick={() => setForgot(!forgot)} className="w-full text-xs text-slate-500 hover:text-cyan-300">{forgot ? "← Back to sign in" : "Forgot password?"}</button>
    </form>
    {!adminOnly && <DemoLogins onPick={(email) => setForm((f) => ({ ...f, email }))} />}
    </>
  );
}
